<template>
  <div class="table-container">
    <!-- SVG Filter for glass distortion effect -->
    <svg style="display: none">
      <filter
          id="glass-distortion"
          x="0%"
          y="0%"
          width="100%"
          height="100%"
          filterUnits="objectBoundingBox"
      >
        <feTurbulence
            type="fractalNoise"
            baseFrequency="0.001 0.005"
            numOctaves="1"
            seed="17"
            result="turbulence"
        />
        <!-- Liked Seeds: 5, 14, 17 -->

        <feComponentTransfer in="turbulence" result="mapped">
          <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
          <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
          <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
        </feComponentTransfer>

        <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />

        <feSpecularLighting
            in="softMap"
            surfaceScale="5"
            specularConstant="1"
            specularExponent="100"
            lighting-color="white"
            result="specLight"
        >
          <fePointLight x="-200" y="-200" z="300" />
        </feSpecularLighting>

        <feComposite
            in="specLight"
            operator="arithmetic"
            k1="0"
            k2="1"
            k3="1"
            k4="0"
            result="litImage"
        />

        <feDisplacementMap
            in="SourceGraphic"
            in2="softMap"
            scale="200"
            xChannelSelector="R"
            yChannelSelector="G"
        />
      </filter>
    </svg>

    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">Loading data...</div>
    </div>

    <div v-else-if="!internalData || internalData.length === 0" class="no-data-message">
      No metering points found matching your criteria, please use filters.
    </div>

    <template v-else>
      <div class="mpids-table table-wrapper">
        <div class="table-header">
          <div
              v-for="column in columns"
              :key="column.id"
              class="table-cell"
              @click="handleSort(column.id)"
              :class="{ 'sortable': column.sortable, 'sorted': sortColumn === column.id }"
          >
            {{ column.label }}
            <span v-if="sortColumn === column.id" class="sort-indicator">
              {{ sortDirection === 'asc' ? '▲' : '▼' }}
            </span>
          </div>
        </div>

        <div
            class="table-row"
            v-for="(item, index) in internalData"
            :key="getRowKey(item, index)"
            @click="handleRowSelect(item)"
            @mouseenter="$emit('row-hover', item)"
            @mouseleave="$emit('row-leave')"
            :class="{ 'selected': selectedRow && selectedRow[rowKey] === item[rowKey] }"
        >
          <div class="liquidGlass-effect"></div>
          <div class="liquidGlass-tint"></div>
          <div class="liquidGlass-shine"></div>

          <div v-for="column in columns" :key="column.id" class="table-cell liquidGlass-text">
            <slot :name="column.id" :item="item" :column="column">
             <span
                 v-if="column.id === 'mpid'"
                 class="clickable-mpid"
                 @click.stop="navigateToMpidDetails(item)"
             >
                {{ item.mpid }}
              </span>

              <span v-else-if="column.id === 'device_attached'">
                <span
                    v-if="item.last_active_device && item.last_active_device.logical_device_number"
                    class="clickable-mpid"
                    @click.stop="navigateToDeviceDetails($event, item.last_active_device)"
                >
                  {{ item.last_active_device.logical_device_number }}
                </span>
                <span v-else>-</span>
              </span>

              <span v-else-if="column.id === 'lastUpdate'">
                {{ formatDate(item.lastUpdate) }}
              </span>

              <span v-else>
                {{ item[column.id] || '-' }}
              </span>
            </slot>
          </div>
        </div>
      </div>

      <div v-if="pagination" class="pagination-controls">
        <div class="pagination-info">
          Showing {{ startItem }}-{{ endItem }} of {{ totalItems }} items
        </div>
        <div class="pagination-buttons">
          <button
              class="pagination-button"
              :disabled="currentPage === 1"
              @click="handlePageChange(currentPage - 1)"
          >
            Previous
          </button>

          <div class="page-numbers">
            <button
                v-for="page in visiblePageNumbers"
                :key="page"
                :class="['page-number', { active: currentPage === page }]"
                @click="handlePageChange(page)"
            >
              {{ page }}
            </button>
          </div>

          <button
              class="pagination-button"
              :disabled="currentPage === totalPages"
              @click="handlePageChange(currentPage + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'MpidTable',
  props: {
    // Data to display in table
    data: {
      type: Array,
      required: true
    },

    // Column configuration (with default columns if not provided)
    columns: {
      type: Array,
      default: () => [
        { id: 'mpid', label: 'MPID', sortable: true },
        { id: 'street', label: 'STREET', sortable: true },
        { id: 'city', label: 'CITY', sortable: true },
        { id: 'zip_code', label: 'ZIP CODE', sortable: true },
        { id: 'lastUpdate', label: 'LAST UPDATE', sortable: true, type: 'date' },
        // --- LABEL MODIFIED HERE ---
        { id: 'device_attached', label: 'DEVICE', sortable: false }
        // --- END MODIFICATION ---
      ]
    },

    // Unique identifier for rows (used for selection)
    rowKey: {
      type: String,
      default: 'mpid'
    },

    // Loading state
    loading: {
      type: Boolean,
      default: false
    },

    // Sort configuration
    sortColumn: {
      type: String,
      default: 'mpid'
    },

    sortDirection: {
      type: String,
      default: 'asc',
      validator: (value) => ['asc', 'desc'].includes(value)
    },

    // Pagination configuration
    pagination: {
      type: Boolean,
      default: true
    },

    currentPage: {
      type: Number,
      default: 1
    },

    pageSize: {
      type: Number,
      default: 10
    },

    totalItems: {
      type: Number,
      default: 0
    },

    // Selected row
    selectedRow: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      // Internal data copy that we'll sort
      internalData: []
    };
  },

  watch: {
    // Update internal data when prop data changes
    data: {
      immediate: true,
      handler(newData) {
        this.internalData = JSON.parse(JSON.stringify(newData || []));
        if (this.sortColumn) {
          this.sortData();
        }
      }
    },

    sortColumn: {
      handler() {
        if (this.sortColumn) {
          this.sortData();
        }
      }
    },

    sortDirection: {
      handler() {
        if (this.sortColumn) {
          this.sortData();
        }
      }
    }
  },

  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    },

    startItem() {
      return this.internalData.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    },

    endItem() {
      return Math.min(this.startItem + this.internalData.length - 1, this.totalItems);
    },

    visiblePageNumbers() {
      const maxVisiblePages = 5;
      const halfVisible = Math.floor(maxVisiblePages / 2);

      let startPage = Math.max(1, this.currentPage - halfVisible);
      let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

      if (endPage === this.totalPages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      const pages = [];
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    }
  },

  methods: {
    handleSort(columnId) {
      const column = this.columns.find(col => col.id === columnId);
      if (!column || !column.sortable) return;

      let direction = 'asc';
      if (this.sortColumn === columnId) {
        direction = this.sortDirection === 'asc' ? 'desc' : 'asc';
      }

      this.$emit('sort', { column: columnId, direction });
    },

    sortData() {
      if (!this.sortColumn || !this.internalData.length) return;
      const column = this.columns.find(col => col.id === this.sortColumn);
      if (!column) return;

      this.internalData.sort((a, b) => {
        let valueA, valueB;

        // Special handling for device_attached
        if (this.sortColumn === 'device_attached') {
          valueA = a.last_active_device && a.last_active_device.logical_device_number ?
              a.last_active_device.logical_device_number : '';
          valueB = b.last_active_device && b.last_active_device.logical_device_number ?
              b.last_active_device.logical_device_number : '';
        } else {
          valueA = this.getValueByPath(a, this.sortColumn);
          valueB = this.getValueByPath(b, this.sortColumn);
        }

        let result;
        if (column.type === 'date') {
          const dateA = valueA ? new Date(valueA).getTime() : 0;
          const dateB = valueB ? new Date(valueB).getTime() : 0;
          result = dateA - dateB;
        } else if (column.type === 'number') {
          const numA = parseFloat(valueA) || 0;
          const numB = parseFloat(valueB) || 0;
          result = numA - numB;
        } else {
          const strA = String(valueA || '').toLowerCase();
          const strB = String(valueB || '').toLowerCase();
          result = strA.localeCompare(strB);
        }

        return this.sortDirection === 'asc' ? result : -result;
      });
    },

    handleRowSelect(row) {
      this.$emit('row-select', row);
    },

    navigateToMpidDetails(item) {
      const mpid = item?.mpid || '';
      if (mpid) {
        this.$router.push(`/mpids/${mpid}`);
      }
    },

    navigateToDeviceDetails(event, device) {
      event.stopPropagation();
      const deviceId = device.logical_device_number || '';
      if (deviceId) {
        this.$router.push(`/devices/${deviceId}`);
      }
    },

    handlePageChange(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.$emit('page-change', page);
      }
    },

    getValueByPath(object, path) {
      if (!object || !path) return '';
      try {
        return path.split('.').reduce((o, p) => (o ? o[p] : ''), object);
      } catch (error) {
        console.error(`Error accessing path ${path}:`, error);
        return '';
      }
    },

    getRowKey(item, index) {
      if (item && this.rowKey && item[this.rowKey] !== undefined) {
        return item[this.rowKey];
      }
      return index;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${hours}:${minutes} ${day}/${month}/${year}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
      }
    }
  }
};
</script>

<style scoped>
.table-container {
  position: relative;
  min-height: 300px;
}

/* Loading indicator */
.loading-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 14px;
  opacity: 0.7;
}

/* No data message */
.no-data-message {
  text-align: center;
  padding: 40px 0;
  font-size: 14px;
  opacity: 0.7;
}

/* MPIDs Table */
.mpids-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 24px;
}

.table-header {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  padding: 10px 0;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.table-header .table-cell {
  cursor: default;
}

.table-header .table-cell.sortable {
  cursor: pointer;
  position: relative;
}

.table-header .table-cell.sortable:hover {
  opacity: 0.8;
}

.sort-indicator {
  margin-left: 4px;
  font-size: 10px;
}

/* Table Row with Liquid Glass Effect */
.table-row {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  padding: 16px 0;
  font-size: 11px;
  position: relative;
  overflow: hidden;

}

/* Liquid Glass Effect Elements - Hidden by default */
.liquidGlass-effect,
.liquidGlass-tint,
.liquidGlass-shine {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.4s cubic-bezier(0.175, 0.885, 0.32, 2.2);
  pointer-events: none;
}

.liquidGlass-effect {
  z-index: 0;
  backdrop-filter: blur(3px);
  filter: url(#glass-distortion);
  isolation: isolate;
}

.liquidGlass-tint {
  z-index: 1;
  background: rgba(255, 255, 255, 0.04);
}

.liquidGlass-shine {
  z-index: 2;

}

/* Apply liquid glass effect on hover and selected states */
.table-row:hover,
.table-row.selected {

  box-shadow: 0 3px 3px rgba(0, 0, 0, 0.02), 0 0 20px rgba(0, 0, 0, 0.05);
}

/* Show glass effect elements on hover and selected */
.table-row:hover .liquidGlass-effect,
.table-row:hover .liquidGlass-tint,
.table-row:hover .liquidGlass-shine,
.table-row.selected .liquidGlass-effect,
.table-row.selected .liquidGlass-tint,
.table-row.selected .liquidGlass-shine {
  opacity: 1;
}

/* Table Cell Styling */
.table-cell {
  flex: 1;
  padding: 0 8px;
  position: relative;
  z-index: 3;
}

/* Text color for liquid glass effect */
.liquidGlass-text {
  color: inherit;
}

.table-row:hover .liquidGlass-text,
.table-row.selected .liquidGlass-text {
  color: white;

}

.clickable-mpid {
  color: #4fc3f7 !important;
  cursor: pointer;
}


/* Pagination controls */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 16px 0;
}

.pagination-info {
  font-size: 11px;
  opacity: 0.7;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-button {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  background: none;
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
}




.page-number:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.1);
}

.page-number.active {
  background-color: rgba(255, 255, 255, 0.2);
  font-weight: 600;
}
</style>